import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'app/auth/services/auth.service';
import { AlertController } from 'app/shared/services/alert-controller.service';
import { DueProspectReminder } from '../interfaces/prospect-reminder';
import { InvitedService } from './invited.service';

/** How often due reminders are checked while the user is signed in. */
export const REMINDER_POLL_INTERVAL_MS = 60_000;

/**
 * Drives the prospect reminder pop-up: polls for due reminders while the user is
 * signed in, shows them one at a time, and acknowledges them.
 *
 * Lifecycle is owned by {@link ProtectedLayoutComponent} — {@link startPolling} on
 * init, {@link stopPolling} on teardown. Nothing runs on the login screen and nothing
 * runs after signing out, so a signed-out tab makes no requests at all.
 *
 * Queue rules:
 * - One reminder on screen at a time. Anything else that comes due waits its turn,
 *   rather than replacing or covering what the user is already looking at.
 * - A reminder is shown at most once per sign-in session ({@link shownThisSession}).
 *   That is what stops the next poll — which may still see it as pending, because the
 *   dismiss write has not landed yet — from popping it a second time. The key includes
 *   the scheduled moment, so re-arming the same prospect for a new time still fires.
 * - The queue always drains: whatever happens to the reminder in front (acknowledged,
 *   saved, failed to save), the next one still gets its turn. A stuck queue would
 *   silently swallow every reminder behind it.
 */
@Injectable({
  providedIn: 'root',
})
export class ProspectRemindersService {
  constructor(
    private readonly invitedService: InvitedService,
    private readonly auth: AuthService,
    private readonly alert: AlertController,
    private readonly router: Router,
  ) { /**/ }

  private pollIntervalId: ReturnType<typeof setInterval> | null = null;

  /** True while a due-check is in flight, so slow responses never stack up. */
  private checking = false;

  /** {@link reminderKey} values already surfaced in this sign-in session. */
  private readonly shownThisSession = new Set<string>();

  /** Due reminders waiting their turn, oldest first. */
  private queue: DueProspectReminder[] = [];

  /** The reminder currently on screen, or `null` when nothing is showing. */
  readonly current = signal<DueProspectReminder | null>(null);

  /**
   * Starts the once-a-minute check, with an immediate first pass so anything that came
   * due while the user was away appears shortly after they sign in. Idempotent.
   */
  startPolling(): void {
    if (this.pollIntervalId != null) return;
    this.checkForDueReminders();
    this.pollIntervalId = setInterval(
      () => this.checkForDueReminders(),
      REMINDER_POLL_INTERVAL_MS,
    );
  }

  /**
   * Stops checking and drops all session state. Anything left unacknowledged is not
   * lost — it is still armed server-side and fires again after the next sign-in.
   */
  stopPolling(): void {
    if (this.pollIntervalId != null) {
      clearInterval(this.pollIntervalId);
      this.pollIntervalId = null;
    }
    this.checking = false;
    this.queue = [];
    this.current.set(null);
    this.shownThisSession.clear();
  }

  /**
   * Acknowledges the reminder on screen and closes it. The user stays where they were.
   *
   * The pop-up closes before the write is confirmed, because it has done its job the
   * moment the user acts. If the write fails the reminder is not re-queued in this
   * session — it fires again on the next full page load, when the server is asked
   * afresh and still reports it as pending. Hence "could", not "will", in the message.
   */
  dismissCurrent(): void {
    const reminder = this.current();
    if (!reminder) return;
    this.current.set(null);

    this.invitedService.dismissReminder(reminder.id).subscribe({
      next: () => this.showNextIfIdle(),
      error: () => {
        this.alert.showToast({
          summary: 'Reminder not dismissed',
          message:
            'We could not reach the server to dismiss this reminder. It could be shown again.',
          severity: 'error',
        });
        this.showNextIfIdle();
      },
    });
  }

  /**
   * Acknowledges the reminder on screen and opens its prospect in a new tab.
   *
   * A new tab rather than this one: the pop-up interrupted the user mid-task, and
   * taking over the current tab would throw away whatever they were in the middle of.
   */
  openCurrentProspect(): void {
    const reminder = this.current();
    if (!reminder) return;
    window.open(this.prospectUrl(reminder), '_blank', 'noopener,noreferrer');
    this.dismissCurrent();
  }

  /**
   * Deep link to the reminder's prospect. Carries the record's status so the landing
   * tab selects the sub-tab that actually holds it — a reminder can be set on any
   * prospect, Open, Won or Lost, and searching the wrong tab finds nothing.
   *
   * Only the business name goes in `search`, since that is the field the list search
   * actually matches on; a record without one lands unfiltered rather than on a search
   * guaranteed to return nothing. Built through the router so names with spaces,
   * ampersands or punctuation are encoded correctly.
   */
  private prospectUrl(reminder: DueProspectReminder): string {
    return this.router.serializeUrl(
      this.router.createUrlTree(['/prospects', 'prospects'], {
        queryParams: {
          prospectStatus: reminder.status,
          prospectId: reminder.id,
          search: (reminder.referred_company ?? '').trim(),
        },
      }),
    );
  }

  /** One due-check. Silent on failure: it simply finds nothing and tries again. */
  private checkForDueReminders(): void {
    if (this.checking) return;
    if (!this.auth.isAuthenticated()) return;

    this.checking = true;
    this.invitedService.getDueReminders().subscribe({
      next: (list) => {
        this.checking = false;
        this.enqueue(list ?? []);
      },
      error: () => {
        this.checking = false;
      },
    });
  }

  /** Appends anything not already shown, queued or on screen, then shows the next. */
  private enqueue(due: DueProspectReminder[]): void {
    for (const reminder of due) {
      const key = this.reminderKey(reminder);
      if (this.shownThisSession.has(key)) continue;
      if (this.current()?.id === reminder.id) continue;
      if (this.queue.some((queued) => this.reminderKey(queued) === key)) continue;
      this.queue = [...this.queue, reminder];
    }
    this.showNextIfIdle();
  }

  /** Promotes the head of the queue, unless something is already on screen. */
  private showNextIfIdle(): void {
    if (this.current()) return;
    const next = this.queue[0];
    if (!next) return;
    this.queue = this.queue.slice(1);
    this.shownThisSession.add(this.reminderKey(next));
    this.current.set(next);
  }

  /**
   * Identity of one *scheduled moment*, not of the prospect.
   *
   * Keying the shown-this-session set on the prospect id alone would mean that once a
   * reminder had been acknowledged, a brand-new reminder set on the same prospect could
   * never fire again without a page reload. Including the date and time keeps the
   * suppression tight to the reminder that was actually shown.
   */
  private reminderKey(reminder: DueProspectReminder): string {
    return `${reminder.id}|${reminder.reminder_date}|${reminder.reminder_time}`;
  }
}
